import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, X, Eye } from "lucide-react";
import { api, media, img } from "../lib/api";
import { pillarMeta } from "../lib/constants";

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  return (
    <section id="videos" className="relative py-24 bg-[#050B14]" data-testid="videos-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-10">
          <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#FF6B00] mb-3">Watch The Show</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">Videos</h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6" data-testid="videos-grid-container">
          {videos.map((v, i) => {
            const meta = pillarMeta(v.category);
            return (
              <motion.button
                key={v.id}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 2) * 0.1 }}
                onClick={() => setActive(v)}
                data-testid={`video-card-${v.id}`}
                className="group relative text-left rounded-xl overflow-hidden border border-[#0066FF]/15 hover:border-[#0066FF]/50 transition-colors"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={img(v.thumbnail_file_id, v.thumbnail_url)}
                    alt={v.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-[#050B14]/40 group-hover:bg-[#050B14]/20 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0066FF]/90 group-hover:scale-110 transition-transform blue-box-glow">
                      <Play className="h-7 w-7 text-white fill-white ml-1" />
                    </span>
                  </div>
                  <span
                    className="absolute top-3 left-3 font-mono2 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full"
                    style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
                  >
                    {meta.short}
                  </span>
                </div>
                <div className="p-5 bg-[#121D33]">
                  <h3 className="font-cond font-bold uppercase text-xl leading-tight mb-1">{v.title}</h3>
                  <p className="text-sm text-[#94A3B8] line-clamp-2 mb-2">{v.description}</p>
                  <div className="flex items-center gap-1.5 text-xs text-[#94A3B8] font-mono2">
                    <Eye className="h-3.5 w-3.5" /> {(v.views || 0).toLocaleString()} views
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="video-modal"
        >
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setActive(null)}
              data-testid="video-modal-close-button"
              className="absolute -top-12 right-0 flex items-center gap-1 text-white hover:text-[#FF6B00] font-cond uppercase"
            >
              <X className="h-6 w-6" /> Close
            </button>
            <video
              src={media(active.video_file_id, active.video_url)}
              controls
              autoPlay
              className="w-full rounded-xl border border-[#0066FF]/30 bg-black"
            />
            <h3 className="mt-4 font-cond font-bold uppercase text-2xl">{active.title}</h3>
            <p className="text-sm text-[#94A3B8] mt-1">{active.description}</p>
          </div>
        </div>
      )}
    </section>
  );
}
